import moment from 'moment';

// This is the zone of offset-free backend timestamps, not the visitor/widget schedule zone.
export const BACKEND_TIMEZONE = process.env.REACT_APP_BACKEND_TIMEZONE || 'Asia/Yerevan';

export function getUserTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (e) {
        return null;
    }
}

const zoneFormatters = new Map();
const parsedTimestamps = new Map();
const pad = (value, length = 2) => String(value).padStart(length, '0');

function zonedParts(date, timezone) {
    if (!zoneFormatters.has(timezone)) {
        zoneFormatters.set(timezone, new Intl.DateTimeFormat('en-GB', {
            timeZone: timezone,
            calendar: 'gregory',
            numberingSystem: 'latn',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
        }));
    }
    const parts = {};
    zoneFormatters.get(timezone).formatToParts(date).forEach(part => {
        if (part.type !== 'literal') parts[part.type] = Number(part.value);
    });
    // Some older Intl implementations use 24 for midnight despite hourCycle.
    if (parts.hour === 24) parts.hour = 0;
    return parts;
}

function partsAsUtc(parts, milliseconds = 0) {
    const date = new Date(0);
    date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
    date.setUTCHours(parts.hour, parts.minute, parts.second, milliseconds);
    return date.getTime();
}

// Interpret backend wall-clock components in their named zone to obtain an instant.
function backendWallTimeToDate(value, timezone) {
    const wallTime = moment.utc(value, moment.ISO_8601, true);
    if (!wallTime.isValid()) return new Date(NaN);
    const wallMillis = wallTime.valueOf();
    const milliseconds = wallTime.millisecond();
    const candidates = [];
    const offsets = new Set();

    // Sample both sides of a possible DST transition, then verify the inverse.
    [-86400000, 0, 86400000].forEach(delta => {
        const probe = new Date(wallMillis + delta);
        offsets.add(partsAsUtc(zonedParts(probe, timezone), milliseconds) - probe.getTime());
    });
    offsets.forEach(offset => {
        const candidate = wallMillis - offset;
        if (partsAsUtc(zonedParts(new Date(candidate), timezone), milliseconds) === wallMillis) {
            candidates.push(candidate);
        }
    });

    // Offset-free repeated DST hours cannot identify which occurrence was intended.
    // Choose the earlier instant consistently; nonexistent local times remain invalid.
    return new Date(candidates.length ? Math.min(...candidates) : NaN);
}

export function fromBackendDate(value, timezone = BACKEND_TIMEZONE) {
    if (value == null || value === '') return new Date(NaN);
    if (value instanceof Date) return new Date(value.getTime());
    if (typeof value === 'number') return new Date(value); // Epoch milliseconds already identify an instant.
    if (typeof value !== 'string') return new Date(NaN);
    const input = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
        // A calendar date is not a backend timestamp. Keep its local calendar day.
        const date = moment(input, 'YYYY-MM-DD', true);
        return date.isValid() ? date.toDate() : new Date(NaN);
    }
    if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(input)) return new Date(NaN);
    if (/(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(input)) {
        const date = moment.parseZone(input, moment.ISO_8601, true);
        return date.isValid() ? date.toDate() : new Date(NaN);
    }
    const cacheKey = `${timezone}:${input}`;
    if (!parsedTimestamps.has(cacheKey)) {
        if (parsedTimestamps.size >= 1000) parsedTimestamps.delete(parsedTimestamps.keys().next().value);
        parsedTimestamps.set(cacheKey, backendWallTimeToDate(input, timezone).getTime());
    }
    return new Date(parsedTimestamps.get(cacheKey));
}

export function backendDateToUtc(value) {
    const date = fromBackendDate(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function formatBackendDateTime(value, locale = 'en-GB', options, fallback = '—') {
    const date = fromBackendDate(value);
    return Number.isNaN(date.getTime()) ? fallback : date.toLocaleString(locale, options);
}

// Convert a user-selected instant to the offset-free datetime expected by the legacy API.
export function toBackendDateTime(date, includeMilliseconds = true) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        throw new Error('A valid Date is required for a backend datetime filter');
    }
    const parts = zonedParts(date, BACKEND_TIMEZONE);
    return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}T` +
        `${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}` +
        (includeMilliseconds ? `.${pad(date.getUTCMilliseconds(), 3)}` : '');
}

export function startOfUserDay(date) {
    const result = new Date(date.getTime());
    result.setHours(0, 0, 0, 0);
    return result;
}

export function endOfUserDay(date) {
    const result = new Date(date.getTime());
    result.setHours(23, 59, 59, 999);
    return result;
}

export function getUserTodayRange(now = new Date()) {
    return {start: startOfUserDay(now), end: endOfUserDay(now)};
}

// Peak-hours responses have no event dates. Preserve their one-hour buckets and
// display them using the relative timezone offsets at the supplied reference instant.
export function peakHoursToUserTime(data = [], referenceDate = new Date()) {
    const backendOffsetMinutes = (partsAsUtc(zonedParts(referenceDate, BACKEND_TIMEZONE),
        referenceDate.getUTCMilliseconds()) - referenceDate.getTime()) / 60000;
    const shift = -referenceDate.getTimezoneOffset() - backendOffsetMinutes;
    return (Array.isArray(data) ? data : []).map(bucket => {
        const [hour, minute] = bucket.name.split(':').map(Number);
        const localMinute = ((hour * 60 + minute + shift) % 1440 + 1440) % 1440;
        return {
            ...bucket,
            name: `${pad(Math.floor(localMinute / 60))}:${pad(localMinute % 60)}`,
        };
    }).sort((a, b) => a.name.localeCompare(b.name));
}

// These are recurring clock-hour filters, not working-hours configuration.
// The legacy API accepts just one pair of backend HH:mm values for the entire range.
export function toBackendHourRange(start, end, referenceDate = new Date()) {
    const validHour = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!validHour.test(start) || !validHour.test(end) || (start === '00:00' && end === '00:00')) {
        return {start, end};
    }
    const startDate = startOfUserDay(referenceDate);
    const endDate = startOfUserDay(referenceDate);
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);
    startDate.setHours(startHour, startMinute, 0, 0);
    if (end < start) endDate.setDate(endDate.getDate() + 1);
    endDate.setHours(endHour, endMinute, 0, 0);
    const backendStart = toBackendDateTime(startDate).slice(11, 16);
    const backendEnd = toBackendDateTime(endDate).slice(11, 16);
    // An enabled, equal-boundary range matches nothing. Do not accidentally turn
    // it into the API's disabled-filter sentinel after conversion to midnight.
    if (backendStart === '00:00' && backendEnd === '00:00') {
        return {start: '00:01', end: '00:01'};
    }
    return {start: backendStart, end: backendEnd};
}

function toLocalDateString(date) {
    const pad = (n) => String(n).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export {toLocalDateString};



// WEBPACK FOOTER //
// ./src/utils/DateUtils.js